/**
 * GuardianOverview — portfolio roll-up. Shows aggregate health,
 * RAG counts, risk heatmap by domain, and a per-company drill-down list.
 */
import { useMemo } from "react";
import { ChevronRight, AlertTriangle } from "lucide-react";
import {
  DOMAINS, portfolioRollup, overallScore, domainScoreMap, rag, hasUnresolvedFlag,
} from "@/lib/guardianData";

const RAG_COLOR = { green: "#16a34a", amber: "#d97706", red: "#dc2626" };
const RAG_BG    = { green: "#dcfce7", amber: "#fef3c7", red: "#fee2e2" };

function heatColor(score) {
  if (score === null || score === undefined) return { bg: "rgba(15,30,46,0.04)", fg: "rgba(15,30,46,0.45)", label: "n/a" };
  // higher score = healthier = greener; lower = warmer
  if (score >= 85) return { bg: "rgba(22,163,74,0.18)", fg: "#15803d" };
  if (score >= 70) return { bg: "rgba(245,180,0,0.18)", fg: "#a16207" };
  if (score >= 55) return { bg: "rgba(217,119,6,0.22)", fg: "#a16207" };
  return { bg: "rgba(220,38,38,0.18)", fg: "#b91c1c" };
}

export default function GuardianOverview({ companies, onSelectCompany }) {
  const rollup = useMemo(() => portfolioRollup(companies), [companies]);

  return (
    <div className="p-6 md:p-8">
      <div className="flex items-baseline justify-between mb-6">
        <div>
          <div className="text-[9px] mb-1" style={{ fontFamily: "ui-monospace, SF Mono, monospace", letterSpacing: "0.18em", color: "rgba(15,30,46,0.5)" }}>/ PORTFOLIO OVERVIEW</div>
          <h2 className="text-2xl font-semibold" style={{ color: "#0F1E2E" }}>Sample Capital · Q1 2026</h2>
          <p className="text-sm text-ink-900/65 mt-1">Roll-up across {rollup.total} companies. Updates whenever you resolve an item below.</p>
        </div>
        <div className="text-right">
          <div className="text-[9px]" style={{ fontFamily: "ui-monospace, SF Mono, monospace", letterSpacing: "0.18em", color: "rgba(15,30,46,0.5)" }}>AVG HEALTH</div>
          <div className="text-4xl font-bold" style={{ color: "#C98F00" }} data-testid="rollup-avg">{rollup.avg}<span className="text-base text-ink-900/40">/100</span></div>
        </div>
      </div>

      {/* RAG strip */}
      <div className="grid grid-cols-4 gap-3 mb-6" data-testid="rollup-rag">
        <Stat label="Companies" value={rollup.total} />
        <Stat label="Green" value={rollup.greens} color={RAG_COLOR.green} />
        <Stat label="Amber" value={rollup.ambers} color={RAG_COLOR.amber} />
        <Stat label="Red"   value={rollup.reds}   color={RAG_COLOR.red} />
      </div>

      {/* Heatmap */}
      <div className="mb-6">
        <div className="text-[10px] mb-2" style={{ fontFamily: "ui-monospace, SF Mono, monospace", letterSpacing: "0.18em", color: "rgba(15,30,46,0.5)" }}>/ RISK HEATMAP · BY DOMAIN</div>
        <div className="grid grid-cols-6 gap-2">
          {DOMAINS.map((d) => {
            const s = rollup.heatmap[d];
            const col = heatColor(s);
            return (
              <div key={d} className="rounded-md py-3 px-2 text-center border" style={{ background: col.bg, borderColor: col.fg + "40" }} data-testid={`heat-${d}`}>
                <div className="text-[10px] font-semibold" style={{ color: "#0F1E2E" }}>{d}</div>
                <div className="text-sm font-bold mt-0.5" style={{ color: col.fg, fontFamily: "ui-monospace, SF Mono, monospace" }}>
                  {s === null ? "n/a" : `${s}%`}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Per-company list */}
      <div>
        <div className="text-[10px] mb-2" style={{ fontFamily: "ui-monospace, SF Mono, monospace", letterSpacing: "0.18em", color: "rgba(15,30,46,0.5)" }}>/ PER-COMPANY DRILL-DOWN</div>
        <ul className="border rounded-md divide-y" style={{ borderColor: "rgba(15,30,46,0.08)" }} data-testid="rollup-list">
          {companies.map((c) => {
            const score = overallScore(c);
            const r = rag(score);
            const flag = hasUnresolvedFlag(c);
            return (
              <li
                key={c.id}
                onClick={() => onSelectCompany(c.id)}
                className="grid grid-cols-[1fr_auto_auto_auto] gap-4 items-center px-4 py-3 hover:bg-cream-50 cursor-pointer transition"
                data-testid={`co-row-${c.id}`}
              >
                <div className="min-w-0">
                  <div className="font-semibold" style={{ color: "#0F1E2E" }}>{c.name} {flag && <AlertTriangle className="inline h-3.5 w-3.5 ml-1" style={{ color: "#dc2626" }} />}</div>
                  <div className="text-[10px]" style={{ fontFamily: "ui-monospace, SF Mono, monospace", letterSpacing: "0.14em", color: "rgba(15,30,46,0.5)" }}>{c.vertical.toUpperCase()} · {c.activeModules.length} MODULES</div>
                </div>
                <div className="text-right font-bold" style={{ color: RAG_COLOR[r] }}>{score}</div>
                <span className="text-[10px] font-semibold px-2 py-0.5 rounded" style={{ background: RAG_BG[r], color: RAG_COLOR[r], fontFamily: "ui-monospace, SF Mono, monospace", letterSpacing: "0.14em" }}>
                  {r.toUpperCase()}
                </span>
                <ChevronRight className="h-4 w-4 text-ink-900/30" />
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}

function Stat({ label, value, color }) {
  return (
    <div className="border rounded-md p-3" style={{ borderColor: "rgba(15,30,46,0.08)", background: "white" }}>
      <div className="text-[9px] mb-1" style={{ fontFamily: "ui-monospace, SF Mono, monospace", letterSpacing: "0.18em", color: "rgba(15,30,46,0.5)" }}>{label.toUpperCase()}</div>
      <div className="text-2xl font-bold" style={{ color: color || "#0F1E2E" }}>{value}</div>
    </div>
  );
}
